import { Controller } from "@hotwired/stimulus"

// Form builder for consultation booking fields. Renders the list of
// custom fields client-side from a hidden JSON input. The user adds /
// removes / reorders rows; on every change we re-serialize back into the
// hidden input so Rails picks the new structure up on submit.
//
// Uses DOM APIs (createElement + textContent) end-to-end so no user
// input ever reaches innerHTML — XSS-safe by construction.
//
// Field shape (matches Consultation::FORM_FIELD_TYPES):
//   { id, type, label, required, options? }
//
export default class extends Controller {
  static targets = ["listEl", "jsonInput", "emptyState"]

  static FIELD_TYPES = [
    { value: "text",     label: "Texte court"      },
    { value: "textarea", label: "Paragraphe"       },
    { value: "email",    label: "Email"            },
    { value: "tel",      label: "Téléphone"        },
    { value: "number",   label: "Nombre"           },
    { value: "select",   label: "Menu déroulant"   },
    { value: "checkbox", label: "Case à cocher"    },
    { value: "address",  label: "Adresse"          },
  ]

  // 7 sous-champs d'adresse — mêmes clés que ADDRESS_SUBFIELDS côté Ruby.
  // Le créateur active/désactive chacun + le marque requis.
  static ADDRESS_SUBFIELDS = [
    { key: "street",          label: "Rue / N°"             },
    { key: "neighborhood",    label: "Quartier"             },
    { key: "city",            label: "Ville"                },
    { key: "governorate",     label: "Gouvernorat"          },
    { key: "postal_code",     label: "Code postal"          },
    { key: "delivery_phone",  label: "Téléphone livraison"  },
    { key: "instructions",    label: "Instructions livraison" },
  ]

  // Préset par défaut quand on crée un nouveau champ adresse — les
  // champs les plus essentiels en TN, le créateur peut ajuster ensuite.
  static ADDRESS_DEFAULT_PRESET = {
    street:          { enabled: true,  required: true  },
    neighborhood:    { enabled: false, required: false },
    city:            { enabled: true,  required: true  },
    governorate:     { enabled: true,  required: true  },
    postal_code:     { enabled: false, required: false },
    delivery_phone:  { enabled: false, required: false },
    instructions:    { enabled: false, required: false },
  }

  connect() {
    this.fields = this.#parseInitial()
    this.render()
  }

  // ─── public actions ────────────────────────────────────────────

  add(event) {
    event.preventDefault()
    const type = event.params?.type || "text"

    const field = {
      id: this.#uuid(),
      type,
      label: type === "address" ? "Adresse de livraison" : "",
      required: false,
    }

    if (type === "select") {
      field.options = [""]
    } else if (type === "address") {
      // Préset des sous-champs (clone profond pour ne pas muter la const).
      const preset = this.constructor.ADDRESS_DEFAULT_PRESET
      field.subfields = {}
      for (const [k, v] of Object.entries(preset)) {
        field.subfields[k] = { enabled: v.enabled, required: v.required }
      }
    }

    this.fields.push(field)
    this.render()
    queueMicrotask(() => {
      const inputs = this.listElTarget.querySelectorAll("input[data-role='label']")
      inputs[inputs.length - 1]?.focus()
    })
  }

  remove(event) {
    event.preventDefault()
    const i = Number(event.currentTarget.dataset.index)
    if (Number.isNaN(i)) return
    this.fields.splice(i, 1)
    this.render()
  }

  moveUp(event) {
    event.preventDefault()
    const i = Number(event.currentTarget.dataset.index)
    if (i <= 0) return
    ;[this.fields[i - 1], this.fields[i]] = [this.fields[i], this.fields[i - 1]]
    this.render()
  }

  moveDown(event) {
    event.preventDefault()
    const i = Number(event.currentTarget.dataset.index)
    if (i >= this.fields.length - 1) return
    ;[this.fields[i + 1], this.fields[i]] = [this.fields[i], this.fields[i + 1]]
    this.render()
  }

  updateField(event) {
    const i = Number(event.currentTarget.dataset.index)
    const role = event.currentTarget.dataset.role
    if (Number.isNaN(i)) return
    const f = this.fields[i]
    if (!f) return

    switch (role) {
      case "label":
        f.label = event.currentTarget.value
        break
      case "required":
        f.required = event.currentTarget.checked
        break
      case "type":
        f.type = event.currentTarget.value
        if (f.type === "select" && (!f.options || f.options.length === 0)) {
          f.options = [""]
        }
        if (f.type !== "select") delete f.options
        if (f.type === "address" && !f.subfields) {
          const preset = this.constructor.ADDRESS_DEFAULT_PRESET
          f.subfields = {}
          for (const [k, v] of Object.entries(preset)) {
            f.subfields[k] = { enabled: v.enabled, required: v.required }
          }
        }
        if (f.type !== "address") delete f.subfields
        this.render()
        return
      case "option": {
        const idx = Number(event.currentTarget.dataset.optionIndex)
        if (Number.isNaN(idx)) return
        f.options[idx] = event.currentTarget.value
        break
      }
      case "sub-enabled": {
        const sub = event.currentTarget.dataset.subKey
        f.subfields ||= {}
        f.subfields[sub] ||= { enabled: false, required: false }
        f.subfields[sub].enabled = event.currentTarget.checked
        // Si on désactive un sous-champ, on désactive aussi son required
        // pour éviter l'état "désactivé mais requis" qui n'a pas de sens.
        if (!event.currentTarget.checked) f.subfields[sub].required = false
        this.render()
        return
      }
      case "sub-required": {
        const sub = event.currentTarget.dataset.subKey
        f.subfields ||= {}
        f.subfields[sub] ||= { enabled: false, required: false }
        f.subfields[sub].required = event.currentTarget.checked
        // On marque aussi enabled à true si l'utilisateur coche requis sur
        // un sous-champ désactivé — sinon état incohérent.
        if (event.currentTarget.checked) f.subfields[sub].enabled = true
        this.render()
        return
      }
    }
    this.serialize()
  }

  addOption(event) {
    event.preventDefault()
    const i = Number(event.currentTarget.dataset.index)
    if (Number.isNaN(i)) return
    const f = this.fields[i]
    f.options = f.options || []
    f.options.push("")
    this.render()
  }

  removeOption(event) {
    event.preventDefault()
    const i = Number(event.currentTarget.dataset.index)
    const idx = Number(event.currentTarget.dataset.optionIndex)
    if (Number.isNaN(i) || Number.isNaN(idx)) return
    this.fields[i].options.splice(idx, 1)
    this.render()
  }

  // ─── rendering (pure DOM, no innerHTML) ────────────────────────

  render() {
    this.emptyStateTarget.style.display = this.fields.length === 0 ? "" : "none"
    // Remove all current rows.
    while (this.listElTarget.firstChild) {
      this.listElTarget.removeChild(this.listElTarget.firstChild)
    }
    this.fields.forEach((f, i) => {
      this.listElTarget.appendChild(this.#buildRow(f, i))
    })
    this.serialize()
  }

  serialize() {
    const cleaned = this.fields.map((f) => {
      const out = { id: f.id, type: f.type, label: f.label, required: !!f.required }
      if (f.type === "select") out.options = (f.options || []).filter((o) => o.trim() !== "")
      if (f.type === "address") {
        out.subfields = {}
        for (const sub of this.constructor.ADDRESS_SUBFIELDS) {
          const s = f.subfields?.[sub.key] || {}
          out.subfields[sub.key] = {
            enabled:  !!s.enabled,
            required: !!s.required,
          }
        }
      }
      return out
    })
    this.jsonInputTarget.value = JSON.stringify(cleaned)
  }

  // ─── DOM helpers ───────────────────────────────────────────────

  #el(tag, attrs = {}, children = []) {
    const node = document.createElement(tag)
    for (const [k, v] of Object.entries(attrs)) {
      if (v == null || v === false) continue
      if (k === "dataset") {
        Object.assign(node.dataset, v)
      } else if (k === "style") {
        node.setAttribute("style", v)
      } else if (k === "class") {
        node.className = v
      } else if (k.startsWith("on")) {
        node.addEventListener(k.slice(2), v)
      } else if (k === "checked" || k === "selected" || k === "disabled") {
        if (v) node.setAttribute(k, "")
      } else {
        node.setAttribute(k, String(v))
      }
    }
    for (const child of [].concat(children)) {
      if (child == null) continue
      if (typeof child === "string") {
        node.appendChild(document.createTextNode(child))
      } else {
        node.appendChild(child)
      }
    }
    return node
  }

  #buildRow(f, i) {
    const handle = this.#el("div", { class: "form-builder-row-handle" }, [
      this.#el("button", {
        type: "button", class: "form-builder-arrow",
        "aria-label": "Monter",
        dataset: { index: String(i), action: "form-builder#moveUp" },
      }, "↑"),
      this.#el("button", {
        type: "button", class: "form-builder-arrow",
        "aria-label": "Descendre",
        dataset: { index: String(i), action: "form-builder#moveDown" },
      }, "↓"),
    ])

    const labelInput = this.#el("input", {
      type: "text", class: "input",
      placeholder: "Libellé de la question",
      value: f.label,
      dataset: { role: "label", index: String(i), action: "input->form-builder#updateField" },
    })

    const typeSelect = this.#el("select", {
      class: "input input-sm",
      dataset: { role: "type", index: String(i), action: "change->form-builder#updateField" },
      style: "max-width: 180px;",
    }, this.constructor.FIELD_TYPES.map((t) => {
      const opt = this.#el("option", { value: t.value }, t.label)
      if (t.value === f.type) opt.selected = true
      return opt
    }))

    const requiredCheckbox = this.#el("input", {
      type: "checkbox",
      checked: !!f.required,
      dataset: { role: "required", index: String(i), action: "change->form-builder#updateField" },
    })

    const requiredLabel = this.#el("label", { class: "form-builder-required" }, [
      requiredCheckbox,
      this.#el("span", {}, "Obligatoire"),
    ])

    const removeBtn = this.#el("button", {
      type: "button", class: "btn btn-ghost btn-sm form-builder-remove",
      "aria-label": "Supprimer ce champ",
      dataset: { index: String(i), action: "form-builder#remove" },
    }, "🗑")

    const top = this.#el("div", { class: "form-builder-row-top" }, [
      labelInput, typeSelect, requiredLabel, removeBtn,
    ])

    const bodyChildren = [top]

    if (f.type === "select") {
      const optionRows = (f.options || []).map((opt, idx) =>
        this.#el("div", { class: "form-builder-option-row" }, [
          this.#el("input", {
            type: "text", class: "input input-sm",
            placeholder: `Option ${idx + 1}`,
            value: opt,
            dataset: {
              role: "option",
              index: String(i),
              optionIndex: String(idx),
              action: "input->form-builder#updateField",
            },
          }),
          this.#el("button", {
            type: "button", class: "btn btn-ghost btn-sm",
            "aria-label": "Supprimer l'option",
            dataset: { index: String(i), optionIndex: String(idx), action: "form-builder#removeOption" },
          }, "×"),
        ])
      )

      const addOptBtn = this.#el("button", {
        type: "button", class: "btn btn-ghost btn-sm",
        dataset: { index: String(i), action: "form-builder#addOption" },
        style: "margin-top: 6px;",
      }, "+ Ajouter une option")

      const optsBlock = this.#el("div", { class: "form-builder-options" }, [
        this.#el("div", { class: "kpi-label", style: "margin-bottom: 8px;" }, "Options du menu"),
        ...optionRows,
        addOptBtn,
      ])
      bodyChildren.push(optsBlock)
    }

    if (f.type === "address") {
      // Tableau des 7 sous-champs avec 2 checkboxes par ligne (activer + requis).
      const subRows = this.constructor.ADDRESS_SUBFIELDS.map((sub) => {
        const cfg = f.subfields?.[sub.key] || { enabled: false, required: false }

        const enableCheckbox = this.#el("input", {
          type: "checkbox",
          checked: !!cfg.enabled,
          dataset: {
            role: "sub-enabled", index: String(i), subKey: sub.key,
            action: "change->form-builder#updateField",
          },
        })
        const requiredCheckbox = this.#el("input", {
          type: "checkbox",
          checked: !!cfg.required,
          disabled: !cfg.enabled,
          dataset: {
            role: "sub-required", index: String(i), subKey: sub.key,
            action: "change->form-builder#updateField",
          },
        })

        return this.#el("div", { class: "form-builder-sub-row" }, [
          this.#el("label", { class: "form-builder-sub-toggle" }, [
            enableCheckbox,
            this.#el("span", { class: "form-builder-sub-label" }, sub.label),
          ]),
          this.#el("label", { class: "form-builder-sub-required" }, [
            requiredCheckbox,
            this.#el("span", {}, "Obligatoire"),
          ]),
        ])
      })

      const subsBlock = this.#el("div", { class: "form-builder-options" }, [
        this.#el("div", { class: "kpi-label", style: "margin-bottom: 8px;" },
          "Sous-champs de l'adresse (cochez ceux à demander)"),
        ...subRows,
      ])
      bodyChildren.push(subsBlock)
    }

    const body = this.#el("div", { class: "form-builder-row-body" }, bodyChildren)

    return this.#el("div", { class: "form-builder-row", dataset: { index: String(i) } }, [
      handle, body,
    ])
  }

  // ─── helpers ───────────────────────────────────────────────────

  #parseInitial() {
    try {
      const raw = this.jsonInputTarget.value || "[]"
      const parsed = JSON.parse(raw)
      if (!Array.isArray(parsed)) return []
      return parsed.map((f) => {
        const out = {
          id: f.id || this.#uuid(),
          type: f.type || "text",
          label: f.label || "",
          required: !!f.required,
          options: Array.isArray(f.options) ? f.options : undefined,
        }
        if (out.type === "address") {
          out.subfields = {}
          const raw = (f.subfields && typeof f.subfields === "object") ? f.subfields : {}
          for (const sub of this.constructor.ADDRESS_SUBFIELDS) {
            const s = raw[sub.key] || {}
            out.subfields[sub.key] = {
              enabled:  !!s.enabled,
              required: !!s.required,
            }
          }
        }
        return out
      })
    } catch (_e) {
      return []
    }
  }

  #uuid() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID()
    return "f-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
  }
}
